import angular from 'angular';
import * as R from 'ramda';

const library = require('@safe-systems/ss-ui-components');

const { copy } = library;

export default function PerformAssessmentDialogController(
  $filter,
  $q,

  alerts,
  confirmCompleteAssessmentDialog,
  ecatDataService,
  ecatSharedModelService,
  loaderService,

  ssDialogService
) {
  const vm = this;

  vm.assessmentNotFinished = false;
  vm.activeTemplate = ecatSharedModelService.getActiveTemplate(ecatSharedModelService.templates);
  vm.activeCategory = vm.activeTemplate.inherentRiskProfiles[0].categories[0];
  vm.activeDomain = vm.activeTemplate.maturityProfiles[0].maturityDomains[0];
  vm.activeContributionComponent = vm.activeDomain.assessmentFactors[0].contributionComponents[0];
  vm.activeComponentLevel = vm.activeContributionComponent.componentLevels[0];
  vm.assessment = vm.resumedAssessment ? vm.resumedAssessment : null;
  vm.newAssessmentName = vm.assessment ? vm.assessment.name : null;
  vm.currentSection = 'risk';

  vm.handleCloseDialog = handleCloseDialog;
  vm.handleNavToCategory = handleNavToCategory;
  vm.handleNavToDomain = handleNavToDomain;
  vm.handleInherentRiskProfileSectionLink = handleInherentRiskProfileSectionLink;
  vm.handleMaturityProfileSectionLink = handleMaturityProfileSectionLink;
  vm.handleExpandContributionComponent = handleExpandContributionComponent;
  vm.handleLevelControlSelectionChange = handleLevelControlSelectionChange;
  vm.handleContributionComponentStatusPill = handleContributionComponentStatusPill;
  vm.handlePerformAssessmentFormSubmit = handlePerformAssessmentFormSubmit;
  vm.handleSaveProgress = handleSaveProgress;

  _activate();

  // //////// METHODS //////////

  function _activate() {
    loaderService.showViewLoader = false;
    const dfdActivate = $q.defer();

    if (vm.assessment) {
      _setInputs().then(dfdActivate.resolve);
    } else {
      dfdActivate.resolve();
    }

    dfdActivate.promise.then(() => {
      loaderService.showViewLoader = false;
    });
  }

  /** ***************************************
   * Event Functions
   */

  function handleCloseDialog() {
    _closeDialog();
  }

  function handleNavToCategory(categoryId) {
    const newActiveCategory = $filter('filter')(
      ecatSharedModelService.getActiveTemplate(
        ecatSharedModelService.templates
      ).inherentRiskProfiles[0].categories,
      { categoryId }
    )[0];

    vm.activeCategory = newActiveCategory;
    _resetContentPaneScroll();
  }

  function handleNavToDomain(maturityDomainId) {
    const newActiveDomain = $filter('filter')(
      ecatSharedModelService.getActiveTemplate(
        ecatSharedModelService.templates
      ).maturityProfiles[0].maturityDomains,
      { maturityDomainId }
    )[0];
    vm.activeDomain = newActiveDomain;
    _resetContentPaneScroll();
  }

  function handleInherentRiskProfileSectionLink() {
    vm.currentSection = 'risk';
  }

  function handleMaturityProfileSectionLink() {
    vm.currentSection = 'maturity';
  }

  function handleExpandContributionComponent(contributionComponent) {
    if (vm.activeContributionComponent !== contributionComponent) {
      vm.activeContributionComponent = contributionComponent;
      vm.activeComponentLevel = contributionComponent.componentLevels[0];
    } else {
      vm.activeContributionComponent = null;
      vm.activeComponentLevel = null;
    }
  }

  function handleContributionComponentStatusPill(contributionComponent, componentLevel) {
    vm.activeContributionComponent = contributionComponent;
    vm.activeComponentLevel = componentLevel;
  }

  function handleLevelControlSelectionChange(componentLevel) {
    _recalculateLevelControlCounts(componentLevel);
  }

  function handlePerformAssessmentFormSubmit($event) {
    _checkFormIsValid().then((formIsValid) => {
      if (formIsValid) {
        loaderService.showViewLoader = true;
        if (vm.saveType === 'saveProgress') {
          _performSave().then(
            () => {
              alerts.success(copy.advisory.ecat.alerts.saveAssessmentProgressSuccessful.text);
            },
            () => {
              alerts.error(copy.advisory.ecat.alerts.saveAssessmentProgressError.text);
            }
          ).finally(() => {
            loaderService.showViewLoader = false;
          });
        } else if (vm.saveType === 'saveAndComplete') {
          _performSave().then(
            () => {
              ecatDataService.checkCanCompleteAssessment(vm.assessment.assessmentId)
                .then((checkCanCompleteAssessmentResponse) => {
                  if (checkCanCompleteAssessmentResponse.data.result) {
                    ecatSharedModelService.confirmCompleteAssessmentDialog = ssDialogService.alert(
                      confirmCompleteAssessmentDialog.getOptions($event, {
                        createNew: false,
                        addCallback: _handleAssessmentCompleted,
                      })
                    );
                    ssDialogService.show(ecatSharedModelService.confirmCompleteAssessmentDialog);
                  } else {
                    alerts.error(copy.advisory.ecat.alerts.markAssessmentCompleteInvalid.text);
                  }
                },
                () => {
                  alerts.error(copy.advisory.ecat.alerts.markAssessmentCompleteError.text);
                })
                .finally(() => {
                  loaderService.showViewLoader = false;
                });
            },
            () => {
              alerts.error(copy.advisory.ecat.alerts.saveAssessmentBeforeMarkingCompleteError.text);
              loaderService.showViewLoader = false;
            }
          );
        }
      }
    });
  }

  function handleSaveProgress() {
    _checkFormIsValid().then((formIsValid) => {
      if (formIsValid) {
        loaderService.showViewLoader = true;
        _performSave().then(
          () => {
            alerts.success(copy.advisory.ecat.alerts.saveAssessmentProgressSuccessful.text);
          },
          () => {
            alerts.error(copy.advisory.ecat.alerts.saveAssessmentProgressError.text);
          }
        ).finally(() => {
          loaderService.showViewLoader = false;
        });
      }
    });
  }

  /** ***************************************
   * Private Functions
   */

  function _setInputs() {
    const dfdSetInputs = $q.defer();
    const dfdSetCategoryFactors = $q.defer();
    const dfdSetLevelControls = $q.defer();
    R.forEach((category, categoryIndex) => {
      const templateCategory = $filter('filter')(
        vm.activeTemplate.inherentRiskProfiles[0].categories,
        { categoryId: category.categoryId }
      )[0];
      templateCategory.numCompleted = 0;
      R.forEach((categoryFactor, categoryFactorIndex) => {
        if (categoryFactor.inherentRiskAssessments.length > 0) {
          const templateCategoryFactor = $filter('filter')(
            templateCategory.categoryFactors,
            { categoryFactorId: categoryFactor.categoryFactorId }
          )[0];
          templateCategoryFactor.newValue = categoryFactor.inherentRiskAssessments[0].categoryFactorOptionId;
          templateCategory.numCompleted += 1;
        }
        if (
          (categoryIndex === vm.assessment.inherentRiskProfiles[0].categories.length - 1)
              && (categoryFactorIndex === category.categoryFactors.length - 1)
        ) {
          dfdSetCategoryFactors.resolve();
        }
      },
      category.categoryFactors);
    },
    vm.assessment.inherentRiskProfiles[0].categories);

    R.forEach((maturityDomain, maturityDomainIndex) => {
      R.forEach((assessmentFactor, assessmentFactorIndex) => {
        R.forEach((contributionComponent, contributionComponentIndex) => {
          R.forEach((componentLevel, componentLevelIndex) => {
            const templateMaturityDomain = $filter('filter')(
              vm.activeTemplate.maturityProfiles[0].maturityDomains,
              { maturityDomainId: maturityDomain.maturityDomainId }
            )[0];
            const templateAssessmentFactor = $filter('filter')(
              templateMaturityDomain.assessmentFactors,
              { assessmentFactorId: assessmentFactor.assessmentFactorId }
            )[0];
            const templateContributionComponent = $filter('filter')(
              templateAssessmentFactor.contributionComponents,
              { contributionComponentId: contributionComponent.contributionComponentId }
            )[0];
            const templateComponentLevel = $filter('filter')(
              templateContributionComponent.componentLevels,
              { componentLevelId: componentLevel.componentLevelId }
            )[0];
            templateComponentLevel.selectedLevelControlsCount = 0;
            templateComponentLevel.note = componentLevel.note; // Store note
            R.forEach((levelControl, levelControlIndex) => {
              if (levelControl.selected) {
                const templateLevelControl = $filter('filter')(
                  templateComponentLevel.levelControls,
                  { levelControlId: levelControl.levelControlId }
                )[0];
                templateLevelControl.isSelected = true;
                templateComponentLevel.selectedLevelControlsCount += 1;
              }
              if (
                (maturityDomainIndex === (vm.assessment.maturityProfiles[0].maturityDomains.length - 1))
                          && (assessmentFactorIndex === (maturityDomain.assessmentFactors.length - 1))
                          && (contributionComponentIndex === (assessmentFactor.contributionComponents.length - 1))
                          && (componentLevelIndex === (contributionComponent.componentLevels.length - 1))
                          && (levelControlIndex === (componentLevel.levelControls.length - 1))
              ) {
                dfdSetLevelControls.resolve();
              }
            },
            componentLevel.levelControls);
          },
          contributionComponent.componentLevels);
        },
        assessmentFactor.contributionComponents);
      },
      maturityDomain.assessmentFactors);
    },
    vm.assessment.maturityProfiles[0].maturityDomains);

    $q.all([
      dfdSetCategoryFactors.promise,
      dfdSetLevelControls.promise,
    ]).then(dfdSetInputs.resolve);

    return dfdSetInputs.promise;
  }

  function _resetContentPaneScroll() {
    angular.element('.ecat--perform-assessment-categories-pane')[0].scrollTop = 0;
    angular.element('.ecat--perform-assessment-domains-pane')[0].scrollTop = 0;
  }

  function _recalculateLevelControlCounts(componentLevel) {
    let selectedLevelControlCount = 0;
    R.forEach(
      (levelControl) => {
        if (levelControl.isSelected) {
          selectedLevelControlCount += 1;
        }
      },
      componentLevel.levelControls
    );
    componentLevel.selectedLevelControlsCount = selectedLevelControlCount;
  }

  function _checkFormIsValid() {
    const dfdCheck = $q.defer();
    let isValid = true;
    if (!vm.newAssessmentName) {
      isValid = false;
    }

    let assessmentId = 0;
    if (!R.isNil(vm.assessment)) {
      if (vm.assessment.assessmentId) {
        assessmentId = vm.assessment.assessmentId;
      }
    }

    ecatDataService.checkAssessmentExists(vm.newAssessmentName, assessmentId)
      .then((checkAssessmentExistsResponse) => {
        if (checkAssessmentExistsResponse.data.result) {
          alerts.error(copy.advisory.ecat.alerts.validateDuplicateAssessmentNameFailed.text);
          isValid = false;
        }
        dfdCheck.resolve(isValid);
      });

    return dfdCheck.promise;
  }

  function _performSave() {
    const dfdSaveAssessment = $q.defer();
    const dfdSaveCategories = $q.defer();
    const dfdSaveMaturityAssessment = $q.defer();
    const factorOptions = _getCategoryFactorOptionsFromDialog();
    const maturities = _getSelectedComponentLevelControlsFromDialog();

    if (vm.assessment && vm.assessment.name === vm.newAssessmentName) {
      // Assessment already saved and no name change has been made
      dfdSaveAssessment.resolve();
    } else {
      // Assessment changes must be saved, default to save new
      let assessmentId = 0;
      if (vm.assessment) {
        // Assessment exists, update
        assessmentId = vm.assessment.assessmentId;
      }
      ecatDataService.saveAssessment(vm.newAssessmentName, assessmentId)
        .then((saveAssessmentResponse) => {
          if (saveAssessmentResponse.data !== 0) {
            // If save is successful, update the UI model
            ecatDataService.getAssessment(saveAssessmentResponse.data)
              .then((getAssessmentResponse) => {
                vm.assessment = getAssessmentResponse.data;
                const existingAssessment = $filter('filter')(
                  ecatSharedModelService.assessments,
                  { assessmentId: vm.assessment.assessmentId }
                )[0];

                if (existingAssessment) {
                  existingAssessment.name = vm.assessment.name;
                } else {
                  ecatSharedModelService.assessments.unshift(vm.assessment);
                }

                dfdSaveAssessment.resolve();
              });
          } else {
            dfdSaveAssessment.reject();
          }
        });
    }

    // After Assessment exists, save Inherent Risk Assessment
    dfdSaveAssessment.promise.then(
      () => {
        ecatDataService.saveBulkInherentRiskAssessment(
          vm.assessment.assessmentId,
          factorOptions
        ).then(() => {
          // If save is successful, update the UI model
          _commitFactorOptionChanges();
          dfdSaveCategories.resolve();
        });

        ecatDataService.saveBulkMaturityAssessment(
          vm.assessment.assessmentId,
          maturities
        ).then(() => {
          dfdSaveMaturityAssessment.resolve();
        });
      },
      () => {
        dfdSaveCategories.reject();
        dfdSaveMaturityAssessment.reject();
      }
    );

    return $q.all([
      dfdSaveCategories.promise,
      dfdSaveMaturityAssessment.promise,
    ]);
  }

  function _getCategoryFactorOptionsFromDialog() {
    const inherentRiskBulkStore = [];
    R.forEach((category) => {
      const riskEntry = {
        categoryId: category.categoryId,
        categoryFactorOptions: [],
      };
      R.forEach((categoryFactor) => {
        if (categoryFactor.newValue) {
          riskEntry.categoryFactorOptions.push(parseInt(categoryFactor.newValue, 10));
        }
      },
      category.categoryFactors);
      inherentRiskBulkStore.push(riskEntry);
    },
    vm.activeTemplate.inherentRiskProfiles[0].categories);
    return inherentRiskBulkStore;
  }

  function _commitFactorOptionChanges() {
    R.forEach((category) => {
      let numCompleted = 0;
      R.forEach((categoryFactor) => {
        categoryFactor.value = categoryFactor.newValue;
        if (categoryFactor.value) {
          numCompleted += 1;
        }
      },
      category.categoryFactors);
      category.numCompleted = numCompleted;
    },
    vm.activeTemplate.inherentRiskProfiles[0].categories);
  }

  function _handleAssessmentCompleted() {
    _closeDialog();
  }

  function _getSelectedComponentLevelControlsFromDialog() {
    const selectedComponentLevels = [];
    R.forEach((maturityDomain) => {
      R.forEach((assessmentFactor) => {
        R.forEach((contributionComponent) => {
          R.forEach((componentLevel) => {
            const currentComponentLevel = {
              componentLevelId: componentLevel.componentLevelId,
              note: componentLevel.note,
              levelControls: [],
            };

            R.forEach((levelControl) => {
              if (levelControl.isSelected) {
                currentComponentLevel.levelControls.push(levelControl.levelControlId);
              }
            },
            componentLevel.levelControls);

            selectedComponentLevels.push(currentComponentLevel);
          },
          contributionComponent.componentLevels);
        },
        assessmentFactor.contributionComponents);
      },
      maturityDomain.assessmentFactors);
    },
    vm.activeTemplate.maturityProfiles[0].maturityDomains);

    return selectedComponentLevels;
  }

  function _closeDialog() {
    ssDialogService.hide(ssDialogService.dialogs.ecat.performAssessmentDialog);
  }
}

PerformAssessmentDialogController.$inject = [
  '$filter',
  '$q',

  'alerts',
  'confirmCompleteAssessmentDialog',
  'ecatDataService',
  'ecatSharedModelService',
  'loaderService',
  'ssDialogService'
];
